import React, { useState } from "react";
import { Button, Col, Loading, MessageBanner, MultiColumnList, Row, TextField, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useIntl, FormattedMessage } from "react-intl";
import { useMutation } from "react-query";
import { generateKult1801Report, startKult1801XlsxJob, } from "../../../api/reportsService";
import { useReportJob } from "../../../hooks/useReportJob";
import { kult1801JobKey } from "../../../utils/reportJobKeys";
import RenderField from "../../../components/RenderField";
/** Minimum supported year for Kult 18-01 report. */
var MIN_YEAR = 1900;
/** Maximum supported year for Kult 18-01 report. */
var MAX_YEAR = 2100;
export var Kult1801ReportSection = function (_a) {
    var _b;
    var publisherId = _a.publisherId;
    var _c = useIntl(), formatMessage = _c.formatMessage, formatNumber = _c.formatNumber;
    var ky = useOkapiKy();
    var _d = useState(String(new Date().getFullYear())), year = _d[0], setYear = _d[1];
    var _e = useState(""), yearError = _e[0], setYearError = _e[1];
    var _f = useState(null), reportData = _f[0], setReportData = _f[1];
    var _g = useState(""), errorMessage = _g[0], setErrorMessage = _g[1];
    var _h = useState(""), successMessage = _h[0], setSuccessMessage = _h[1];
    // the XLSX export runs as an agency report job (DB-876): „Generuje se…" -> „Stáhnout"
    // one job per publisher and year - a job of another year must not offer its file for the year now shown
    var xlsxJob = useReportJob(kult1801JobKey(publisherId, year), ky);
    var validateYear = function () {
        var num = Number(year);
        if (!Number.isInteger(num) || num < MIN_YEAR || num > MAX_YEAR) {
            setYearError(formatMessage({
                id: "ui-br-publishers.kult-18-01.validation.year.invalid",
            }));
            return false;
        }
        setYearError("");
        return true;
    };
    var previewMutation = useMutation({
        mutationFn: function (params) {
            return generateKult1801Report(ky, params);
        },
        onMutate: function () {
            setReportData(null);
            setErrorMessage("");
            setSuccessMessage("");
        },
        onSuccess: function (data) {
            setReportData(data);
            setSuccessMessage(formatMessage({ id: "ui-br-publishers.kult-18-01.success.preview" }));
        },
        onError: function () {
            setReportData(null);
            setErrorMessage(formatMessage({ id: "ui-br-publishers.kult-18-01.error.generate" }));
        },
    });
    var xlsxMutation = useMutation({
        mutationFn: function (params) {
            return startKult1801XlsxJob(ky, params);
        },
        onMutate: function () {
            xlsxJob.reset();
            setErrorMessage("");
            setSuccessMessage("");
        },
        onSuccess: function (started) {
            xlsxJob.start(started);
        },
        onError: function () {
            setErrorMessage(formatMessage({ id: "ui-br-publishers.kult-18-01.error.generate" }));
        },
    });
    var downloadMutation = useMutation({
        mutationFn: function () { return xlsxJob.download(); },
        onMutate: function () {
            setErrorMessage("");
            setSuccessMessage("");
        },
        onSuccess: function () {
            setSuccessMessage(formatMessage({ id: "ui-br-publishers.kult-18-01.success.xlsx" }));
        },
        onError: function () {
            setErrorMessage(formatMessage({ id: "ui-br-publishers.report-job.error.download" }));
        },
    });
    var jobErrorMessage = "";
    if (xlsxJob.isFailed) {
        jobErrorMessage = formatMessage({
            id: "ui-br-publishers.kult-18-01.error.generate",
        });
    }
    else if (xlsxJob.accessDenied) {
        jobErrorMessage = formatMessage({
            id: "ui-br-publishers.report-job.error.forbidden",
        });
    }
    else if (xlsxJob.pollFailed) {
        jobErrorMessage = formatMessage({
            id: "ui-br-publishers.report-job.error.status",
        });
    }
    var handleYearChange = function (e) {
        setYear(e.target.value);
        setReportData(null);
    };
    var handlePreview = function () {
        if (!validateYear())
            return;
        previewMutation.mutate({ publisherId: publisherId, year: Number(year) });
    };
    var handleXlsx = function () {
        if (!validateYear())
            return;
        xlsxMutation.mutate({ publisherId: publisherId, year: Number(year) });
    };
    var isBusy = previewMutation.isLoading || xlsxMutation.isLoading || xlsxJob.isGenerating;
    var productColumns = [
        "rowNumber",
        "typeName",
        "titleCount",
    ];
    var productFormatter = {
        rowNumber: function (row) { return row.rowNumber; },
        typeName: function (row) { return row.typeName; },
        titleCount: function (row) { return formatNumber(row.titleCount); },
    };
    var productColumnMapping = {
        rowNumber: formatMessage({
            id: "ui-br-publishers.kult-18-01.column.rowNumber",
        }),
        typeName: formatMessage({
            id: "ui-br-publishers.kult-18-01.column.typeName",
        }),
        titleCount: formatMessage({
            id: "ui-br-publishers.kult-18-01.column.titleCount",
        }),
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.description" })))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 3 },
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-publishers.kult-18-01.field.year",
                    }), value: year, onChange: handleYearChange, required: true, error: yearError || undefined }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(Button, { buttonStyle: "primary", onClick: handlePreview, disabled: isBusy },
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.button.preview" })),
                React.createElement(Button, { buttonStyle: "primary", onClick: handleXlsx, disabled: isBusy },
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.button.downloadXlsx" })),
                xlsxJob.isGenerating && (React.createElement("span", { role: "status", "aria-live": "polite" },
                    React.createElement(Loading, null),
                    " ",
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.report-job.generating" }))),
                xlsxJob.isDone && (React.createElement(Button, { buttonStyle: "success", onClick: function () { return downloadMutation.mutate(); }, disabled: downloadMutation.isLoading },
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.report-job.button.download" }))))),
        jobErrorMessage && (React.createElement(MessageBanner, { type: "error" }, jobErrorMessage)),
        successMessage && (React.createElement(MessageBanner, { type: "success", dismissable: true, onExited: function () { return setSuccessMessage(""); } }, successMessage)),
        errorMessage && (React.createElement(MessageBanner, { type: "error", dismissable: true, onExited: function () { return setErrorMessage(""); } }, errorMessage)),
        reportData && (React.createElement(React.Fragment, null,
            React.createElement("h3", null,
                React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.results.title", values: { year: reportData.year } })),
            React.createElement("h4", null,
                React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.section.identification" })),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 6 },
                    React.createElement(RenderField, { label: formatMessage({
                            id: "ui-br-publishers.kult-18-01.field.kultLegalForm",
                        }), value: reportData.publisherInfo.kultLegalFormCode
                            ? "".concat(reportData.publisherInfo.kultLegalFormCode, " \u2014 ").concat((_b = reportData.publisherInfo.kultLegalFormName) !== null && _b !== void 0 ? _b : "")
                            : "" })),
                React.createElement(Col, { xs: 12, md: 6 },
                    React.createElement(RenderField, { label: formatMessage({
                            id: "ui-br-publishers.kult-18-01.field.czNace",
                        }), value: reportData.publisherInfo.czNace }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 6 },
                    React.createElement(RenderField, { label: formatMessage({
                            id: "ui-br-publishers.kult-18-01.field.dataBox",
                        }), value: reportData.publisherInfo.dataBoxIsds }))),
            React.createElement("h4", null,
                React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.section.partI" })),
            React.createElement(MultiColumnList, { contentData: reportData.productRows, formatter: productFormatter, visibleColumns: productColumns, columnMapping: productColumnMapping, interactive: false }),
            React.createElement("h4", null,
                React.createElement(FormattedMessage, { id: "ui-br-publishers.kult-18-01.section.partV" })),
            React.createElement(MultiColumnList, { contentData: reportData.fepRows, formatter: productFormatter, visibleColumns: productColumns, columnMapping: productColumnMapping, interactive: false })))));
};
